import {Record} from 'immutable';

class MediaPlacement extends Record<{
    AudioHostUrl: string;
    ScreenDataUrl: string;
    ScreenSharingUrl: string;
    ScreenViewingUrl: string;
    SignalingUrl: string;
    TurnControlUrl: string;
}>({
    AudioHostUrl: '',
    ScreenDataUrl: '',
    ScreenSharingUrl: '',
    ScreenViewingUrl: '',
    SignalingUrl: '',
    TurnControlUrl: '',
}) {}

class AwsMeeting extends Record<{
    MeetingId: string;
    MediaPlacement: MediaPlacement;
    MediaRegion: string;
}>({
    MeetingId: '',
    MediaPlacement: new MediaPlacement({}),
    MediaRegion: '',
}) {}

class CreatedAt extends Record<{
    date: string;
    timezoneType?: number;
    timezone: string;
}>({
    date: '',
    timezoneType: undefined,
    timezone: '',
}) {}

class Meeting extends Record<{
    famlyMeetingId: string;
    awsMeetingId: string;
    createdAt: CreatedAt;
    createdBy: string;
    awsMeeting: AwsMeeting;
}>({
    famlyMeetingId: '',
    awsMeetingId: '',
    createdAt: new CreatedAt({}),
    createdBy: '',
    awsMeeting: new AwsMeeting({}),
}) {}

export class Attendee extends Record<{
    image: string;
    loginId: string;
    name: string;
    status: string;
}>({
    image: '',
    loginId: '',
    name: '',
    status: '',
}) {}

class AwsAttendee extends Record<{
    AttendeeId: string;
    ExternalUserId: string;
    JoinToken: string;
}>({
    AttendeeId: '',
    ExternalUserId: '',
    JoinToken: '',
}) {}

class Me extends Record<{
    attendeeId: string;
    awsAttendee: AwsAttendee;
    createdAt: string;
    famlyMeetingId: string;
    loginId: string;
    attendee: Attendee;
}>({
    attendeeId: '',
    awsAttendee: new AwsAttendee({}),
    createdAt: '',
    famlyMeetingId: '',
    loginId: '',
    attendee: new Attendee({}),
}) {}

interface AttendeeLoginIdMap {
    [boundAttendeeId: string]: string;
}
export class FamlyMeeting extends Record<{
    meeting: Meeting;
    me?: Me;
    attendees: Attendee[];
    externalJoinUrl: string | null;
    refreshToken: string | null;
    attendeeLoginIdMap: AttendeeLoginIdMap;
}>({
    meeting: new Meeting({}),
    me: undefined,
    attendees: [],
    externalJoinUrl: null,
    refreshToken: null,
    attendeeLoginIdMap: {},
}) {}

export const famlyMeetingFromAPI = (data: any) => {
    return new FamlyMeeting({
        ...data,
        attendees: data.attendees?.map((attendee: any) => new Attendee({...attendee})) ?? [],
        me: data.me
            ? new Me({
                  ...data.me,
                  attendee: new Attendee({...data.me.asSimpleLogin}),
                  awsAttendee: new AwsAttendee({...data.me.awsAttendee}),
              })
            : undefined,
        meeting: new Meeting({
            ...data.meeting,
            createdAt: new CreatedAt({...data.meeting.createdAt, timezoneType: data.meeting.createdAt.timezone_type}),
            awsMeeting: new AwsMeeting({
                ...data.meeting.awsMeeting,
                MediaPlacement: new MediaPlacement({...data.meeting.awsMeeting.MediaPlacement}),
            }),
        }),
    });
};

export interface DropdownOption {
    value: string;
    label: string;
}

export interface TileState {
    tileId: number | null;
    localTile: boolean;
    localTileStarted: boolean;
    isContent: boolean;
    active: boolean;
    paused: boolean;
    poorConnection: boolean;
    boundAttendeeId: string | null;
    boundExternalUserId: string | null; // Same as the famly loginId
    boundVideoStream: MediaStream | null;
    boundVideoElement: HTMLVideoElement | null;
    nameplate: string | null;
    videoStreamContentWidth: number | null;
    videoStreamContentHeight: number | null;
    videoElementCSSWidthPixels: number | null;
    videoElementCSSHeightPixels: number | null;
    devicePixelRatio: number;
    videoElementPhysicalWidthPixels: number | null;
    videoElementPhysicalHeightPixels: number | null;
    streamId: number | null;
}

export enum DEVICE_NOT_FOUND {
    AUDIO_INPUT = 'AUDIO_INPUT',
    AUDIO_OUTPUT = 'AUDIO_OUTPUT',
    VIDEO_INPUT = 'VIDEO_INPUT',
}

export interface DeviceNotFoundErrors {
    audioInput: boolean;
    audioOutput: boolean;
    videoInput: boolean;
}

export enum MEETING_STATE {
    DEVICE_SUPPORTED = 'DEVICE_SUPPORTED',
    EXPIRED = 'EXPIRED',
    JOINING = 'JOINING',
    ACTIVE = 'ACTIVE',
    FETCHING_EXTERNAL_JOIN_URL = 'FETCHING_EXTERNAL_JOIN_URL',
    READY_TO_JOIN = 'READY_TO_JOIN',
}

export interface AttendeeVolumeInfo {
    boundAttendeeId: string;
    volume?: number; // A fraction between 0 and 1
    muted?: boolean;
    signalStrength?: number; // 0 (no signal), 0.5 (weak), 1 (strong)
}
